import React, { useState, useRef } from 'react';
import { toast } from 'sonner';
import { Mic, MicOff, Loader2, CheckCircle2, XCircle } from 'lucide-react';

/**
 * Microphone button that records the user, sends the audio to the backend voice-check
 * endpoint, and shows a ✓/✗ result. Works in iOS Safari via MediaRecorder.
 *
 * Props:
 *  - expected: the Arabic text the user is supposed to say (e.g., "بَ")
 *  - apiClient: axios-like client with .post(url, formData, config)
 *  - duration: recording length in milliseconds (default 2500)
 *  - testId: data-testid prefix
 *  - size: 'sm' | 'md'
 */
const VoiceRecorder = ({ expected, apiClient, duration = 2500, testId = 'voice-recorder', size = 'sm' }) => {
  const [state, setState] = useState('idle'); // idle | recording | checking | ok | bad | unsupported
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const supported = typeof window !== 'undefined'
    && typeof navigator !== 'undefined'
    && navigator.mediaDevices?.getUserMedia
    && (window.MediaRecorder !== undefined);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const start = async (e) => {
    e?.stopPropagation();
    if (!supported) {
      setState('unsupported');
      toast.error('Tarayıcınız mikrofon desteklemiyor');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4',
      });
      recorderRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (ev) => {
        if (ev.data.size > 0) chunksRef.current.push(ev.data);
      };
      mr.onstop = async () => {
        stopStream();
        const blob = new Blob(chunksRef.current, { type: mr.mimeType });
        if (blob.size === 0) {
          setState('idle');
          toast.error('Ses kaydedilemedi, tekrar deneyin');
          return;
        }
        setState('checking');
        const form = new FormData();
        form.append('expected', expected);
        form.append(
          'audio',
          blob,
          mr.mimeType.includes('mp4') ? 'speech.mp4' : 'speech.webm'
        );
        try {
          const res = await apiClient.post('/elifba/voice-check', form, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          const ok = !!res.data.match;
          setState(ok ? 'ok' : 'bad');
          toast[ok ? 'success' : 'error'](
            ok
              ? `Doğru ✓ (eşleşme: %${Math.round((res.data.score || 0) * 100)})`
              : `Tekrar dene: "${res.data.transcribed || ''}" duyuldu`,
            { duration: 2000 }
          );
        } catch (err) {
          setState('idle');
          toast.error('Onaylama servisi şu an erişilemez');
        } finally {
          setTimeout(() => setState('idle'), 2000);
        }
      };
      setState('recording');
      mr.start();
      // Auto-stop after duration
      setTimeout(() => {
        if (recorderRef.current && recorderRef.current.state === 'recording') {
          recorderRef.current.stop();
        }
      }, duration);
    } catch (err) {
      stopStream();
      setState('idle');
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        toast.error('Mikrofon izni reddedildi');
      } else {
        toast.error('Mikrofona erişilemedi');
      }
    }
  };

  if (!supported) return null;

  const sizes = {
    sm: 'w-7 h-7',
    md: 'w-9 h-9',
  };
  const iconSize = size === 'md' ? 'w-4 h-4' : 'w-3.5 h-3.5';

  const stateClass = {
    idle: 'bg-rose-500 hover:bg-rose-600 text-white',
    recording: 'bg-red-600 text-white animate-pulse',
    checking: 'bg-stone-400 text-white cursor-wait',
    ok: 'bg-emerald-600 text-white',
    bad: 'bg-rose-700 text-white',
    unsupported: 'bg-stone-300 text-stone-500',
  }[state];

  const icon = {
    idle: <Mic className={iconSize} />,
    recording: <MicOff className={iconSize} />,
    checking: <Loader2 className={`${iconSize} animate-spin`} />,
    ok: <CheckCircle2 className={iconSize} />,
    bad: <XCircle className={iconSize} />,
    unsupported: <MicOff className={iconSize} />,
  }[state];

  return (
    <button
      onClick={start}
      disabled={state === 'recording' || state === 'checking'}
      className={`flex items-center justify-center ${sizes[size]} rounded-full transition-all ${stateClass}`}
      title="Sesli tekrar et"
      data-testid={`${testId}-${state}`}
    >
      {icon}
    </button>
  );
};

export default VoiceRecorder;
